// ================================
// IMPORTS
// ================================
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type { Address } from "@types"

// ================================
// TYPES
// ================================

/**
 * Résultat de recherche pour un bâtiment (format API)
 */
export type SearchBuildingResult = {
    /** Identifiant du bâtiment */
    _id: string
    /** Nom du bâtiment */
    name?: string
    /** Adresse du bâtiment */
    address?: Partial<Address> | string
}

/**
 * Résultat de recherche pour une source (format API)
 */
export type SearchSourceResult = {
    /** Identifiant de la source */
    _id_source: string
    /** Type de source (pdl, pce, etc.) */
    idtype: string
    /** Identifiant réel de la source (PDL, PCE, etc.) */
    idsource: string
    /** Opérateur (enedis, grdf, etc.) */
    operator?: string
    /** Détails de la source */
    details?: Record<string, unknown>
    /** Nom de la source */
    name?: string
}

/**
 * Résultat de recherche pour un dossier (format API)
 */
export type SearchFolderResult = {
    /** Identifiant du dossier */
    _id: string
    /** Nom du dossier */
    name: string
    /** Score de pertinence */
    score?: number
}

/**
 * Réponse de l'API de recherche globale
 */
export type SearchResponse = {
    /** Résultats des bâtiments */
    buildings: { data: SearchBuildingResult[]; max: number }
    /** Résultats des sources */
    sources: { data: SearchSourceResult[]; max: number }
    /** Résultats des dossiers */
    folders: { data: SearchFolderResult[]; max: number }
}

// ================================
// SERVICE
// ================================

/**
 * Service permettant d'effectuer une recherche globale (bâtiments, sources, dossiers).
 * Utilise l'API GET /api/metier/v5/search.
 */
export class SearchService {
    /**
     * Recherche globale (bâtiments, sources, dossiers)
     * @param search - Texte de recherche (min 3 caractères côté API)
     * @param limit - Nombre max de résultats par catégorie (défaut: 5)
     * @returns Résultats groupés par type (buildings, sources, folders)
     */
    async search(search: string, limit: number = 5): Promise<SearchResponse> {
        return handleServiceCall<SearchResponse>(async () => {
            const { data } = await axios.get<SearchResponse>(
                "/api/metier/v5/search",
                {
                    params: { text: search, limit },
                }
            )
            return data
        })
    }
}
